import { Field, Form, Formik } from "formik";
import { useSelector } from "react-redux";
import {
  getCompanyBranding,
  createCompanyBranding,
  updateCompanyBranding,
  selectCompanyBranding,
  selectIsBrandingUpdate,
  selectIsBusy,
} from "./CompanyBrandingSlice";
import * as Yup from "yup";
import "./CompanyBrandingForm.scss";
import { useEffect, useMemo, useState } from "react";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { UpdateCompanyBrandingModel } from "services/classes/UpdateCompanyBrandingModel";
import { ExistingFile } from "cpl-react-common/components/Inputs/fileUpload/components/FilePreview";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import { CPLFileUpload } from "app/modules/shared/cplFileUpload/CPLFileUpload";
import CompanyBrandingPreview from "./CompanyBrandingPreview";
import { Permission } from "services/userSession/UserPermittedComponent";
import { Permissions } from "cpl-react-common/components/Enums/Permissions";

const companyBrandingSchema = Yup.object().shape({
  isGeneratedCSSFile: Yup.boolean().required(),
});

const CompanyBrandingForm = () => {
  const thunkDispatch = useThunkDispatch();
  const [isPreviewOpen, setPreviewOpen] = useState(false);
  const [logoLocalURL, setLogoLocalURL] = useState<string | null | undefined>(undefined);
  const [headerFontLocalURL, setHeaderFontLocalURL] = useState<string | null | undefined>(undefined);
  const [bodyFontLocalURL, setBodyFontLocalURL] = useState<string | null | undefined>(undefined);

  const isBusy = useSelector(selectIsBusy);
  const branding = useSelector(selectCompanyBranding);
  const isUpdating = useSelector(selectIsBrandingUpdate);

  const initialLogo = useMemo(() => {
    return isUpdating && branding.logoUrl != undefined
      ? [
          {
            id: branding.companyBrandingID,
            name: "Logo",
            size: 0,
            previewURL: branding.logoUrl,
          } as ExistingFile,
        ]
      : [];
  }, [branding, isUpdating]);

  const initialFavicon = useMemo(() => {
    return isUpdating && branding.faviconUrl != undefined
      ? [
          {
            id: branding.companyBrandingID,
            name: "Favicon",
            size: 0,
            previewURL: branding.faviconUrl,
          } as ExistingFile,
        ]
      : [];
  }, [branding, isUpdating]);

  const initialBodyFont = useMemo(() => {
    return isUpdating && branding.bodyFontUrl != undefined
      ? [
          {
            id: branding.companyBrandingID,
            name: "Body Font",
            size: 0,
            previewURL: branding.bodyFontUrl,
          } as ExistingFile,
        ]
      : [];
  }, [branding, isUpdating]);

  const initialHeaderFont = useMemo(() => {
    return isUpdating && branding.headerFontUrl != undefined
      ? [
          {
            id: branding.companyBrandingID,
            name: "Header Font",
            size: 0,
            previewURL: branding.headerFontUrl,
          } as ExistingFile,
        ]
      : [];
  }, [branding, isUpdating]);

  const initialCssFile = useMemo(() => {
    return isUpdating && branding.companyBrandingFileUrl != undefined
      ? [
          {
            id: branding.companyBrandingID,
            name: "CSS File",
            size: 0,
            previewURL: branding.companyBrandingFileUrl,
          } as ExistingFile,
        ]
      : [];
  }, [branding, isUpdating]);

  useEffect(() => {
    thunkDispatch(getCompanyBranding({}));
    // eslint-disable-next-line
  }, []);

  async function saveCompanyBranding(newValue: UpdateCompanyBrandingModel) {
    if (!isUpdating) {
      await thunkDispatch(createCompanyBranding({ companyBranding: newValue }));
      thunkDispatch(getCompanyBranding({}));
    } else {
      newValue.companyBrandingID = branding.companyBrandingID;
      thunkDispatch(updateCompanyBranding({ companyBranding: newValue }));
    }
  }

  return (
    <div className="card">
      <ActivityIndicator isBusy={isBusy}>
        <Formik
          enableReinitialize
          initialValues={branding}
          onSubmit={(values) => saveCompanyBranding(values)}
          validationSchema={companyBrandingSchema}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <div className="floating-toolbar">
                <button
                  data-test-id="company-branding-form__open-preview-button"
                  className="btn btn-primary preview-open-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    setPreviewOpen(true);
                  }}
                >
                  Open Preview
                </button>
                <Permission permissionPredicate={(permission) => permission === Permissions.EditCompanyBranding}>
                  {isUpdating ? (
                    <button
                      data-test-id="company-branding-form__save-button"
                      className="btn btn-primary btn icon"
                      type="submit"
                    >
                      Save
                    </button>
                  ) : (
                    <button
                      data-test-id="company-branding-form__create-button"
                      className="btn btn-success btn icon"
                      type="submit"
                    >
                      Create
                    </button>
                  )}
                </Permission>
              </div>
              <div className="card-header">
                <div className="card-title">Company Branding</div>
                <div className="card-toolbar">
                  <div className="form-switch form-switch-md form-check-custom form-check-solid pe-2">
                    <label className="fw-bold text-muted form-label col-form-label pe-2">Generate style sheet?</label>
                    <Field
                      data-test-id="company-branding-form__generate-style-sheet-checkbox"
                      name={"isGeneratedCSSFile"}
                      title="Generate style sheet?"
                      type="checkbox"
                      className="form-check-input"
                    />
                  </div>
                </div>
              </div>
              <div className="card-body">
                <div className="accordion" id="brandingSettingsAccordion">
                  <CompanyBrandingSection
                    data-test-id="company-branding-form__global-toggle-button"
                    sectionName="global"
                    label="Global"
                    hidden={!values?.isGeneratedCSSFile}
                  >
                    <Field
                      data-test-id="company-branding-form__client-portal-name-input"
                      name={"clientPortalName"}
                      type="text"
                      className="form-control"
                    />
                  </CompanyBrandingSection>
                  <CompanyBrandingSection
                    data-test-id="company-branding-form__brand-colours-toggle-button"
                    sectionName="brandColours"
                    label="Brand Colours"
                    hidden={!values?.isGeneratedCSSFile}
                  >
                    <CompanyBrandingColourPicker
                      data-test-id="company-branding-form__brand-primary-colour-input"
                      name="brandPrimaryColour"
                      label="Primary Colour"
                      value={values?.brandPrimaryColour ?? "#FFFFFF"}
                    />
                    <CompanyBrandingColourPicker
                      data-test-id="company-branding-form__brand-secondary-colour-input"
                      name="brandSecondaryColour"
                      label="Secondary Colour"
                      value={values?.brandSecondaryColour ?? "#FFFFFF"}
                    />
                    <CompanyBrandingColourPicker
                      data-test-id="company-branding-form__brand-tertiary-colour-input"
                      name="brandTertiaryColour"
                      label="Tertiary Colour"
                      value={values?.brandTertiaryColour ?? "#FFFFFF"}
                    />
                  </CompanyBrandingSection>
                  <CompanyBrandingSection
                    data-test-id="company-branding-form__navbar-toggle-button"
                    sectionName="navbar"
                    label="Navigation Bar"
                    hidden={!values?.isGeneratedCSSFile}
                  >
                    <CompanyBrandingColourPicker
                      data-test-id="company-branding-form__navbar-background-colour-input"
                      name="navbarBackgroundColour"
                      label="Background Colour"
                      value={values?.navbarBackgroundColour ?? "#FFFFFF"}
                    />
                    <CompanyBrandingColourPicker
                      data-test-id="company-branding-form__navbar-text-colour-input"
                      name="navbarTextColour"
                      label="Text Colour"
                      value={values?.navbarTextColour ?? "#FFFFFF"}
                    />
                    <CompanyBrandingColourPicker
                      data-test-id="company-branding-form__navbar-button-colour-input"
                      name="navbarButtonColour"
                      label="Button Colour"
                      value={values?.navbarButtonColour ?? "#FFFFFF"}
                    />
                    <CompanyBrandingColourPicker
                      data-test-id="company-branding-form__navbar-button-text-colour-input"
                      name="navbarButtonTextColour"
                      label="Button Text Colour"
                      value={values?.navbarButtonTextColour ?? "#FFFFFF"}
                    />
                    <CompanyBrandingColourPicker
                      data-test-id="company-branding-form__navbar-icon-colour-input"
                      name="navbarIconColour"
                      label="Icon Colour"
                      value={values?.navbarIconColour ?? "#FFFFFF"}
                    />
                  </CompanyBrandingSection>
                  <CompanyBrandingSection
                    data-test-id="company-branding-form__footer-toggle-button"
                    sectionName="footer"
                    label="Footer"
                    hidden={!values?.isGeneratedCSSFile}
                  >
                    <CompanyBrandingColourPicker
                      data-test-id="company-branding-form__footer-background-colour-input"
                      name="footerBackgroundColour"
                      label="Background Colour"
                      value={values?.footerBackgroundColour ?? "#FFFFFF"}
                    />
                    <CompanyBrandingColourPicker
                      data-test-id="company-branding-form__footer-text-colour-input"
                      name="footerTextColour"
                      label="Text Colour"
                      value={values?.footerTextColour ?? "#FFFFFF"}
                    />
                    <CompanyBrandingColourPicker
                      data-test-id="company-branding-form__footer-button-colour-input"
                      name="footerButtonColour"
                      label="Button Colour"
                      value={values?.footerButtonColour ?? "#FFFFFF"}
                    />
                    <CompanyBrandingColourPicker
                      data-test-id="company-branding-form__footer-button-text-colour-input"
                      name="footerButtonTextColour"
                      label="Button Text Colour"
                      value={values?.footerButtonTextColour ?? "#FFFFFF"}
                    />
                  </CompanyBrandingSection>
                  <CompanyBrandingSection
                    data-test-id="company-branding-form__toolbar-toggle-button"
                    sectionName="toolbar"
                    label="Toolbar"
                    hidden={!values?.isGeneratedCSSFile}
                  >
                    <CompanyBrandingColourPicker
                      data-test-id="company-branding-form__toolbar-background-colour-input"
                      name="toolbarBackgroundColour"
                      label="Background Colour"
                      value={values?.toolbarBackgroundColour ?? "#FFFFFF"}
                    />
                    <CompanyBrandingColourPicker
                      data-test-id="company-branding-form__toolbar-text-colour-input"
                      name="toolbarTextColour"
                      label="Text Colour"
                      value={values?.toolbarTextColour ?? "#FFFFFF"}
                    />
                    <CompanyBrandingColourPicker
                      data-test-id="company-branding-form__toolbar-button-colour-input"
                      name="toolbarButtonColour"
                      label="Button Colour"
                      value={values?.toolbarButtonColour ?? "#FFFFFF"}
                    />
                    <CompanyBrandingColourPicker
                      data-test-id="company-branding-form__toolbar-button-text-colour-input"
                      name="toolbarButtonTextColour"
                      label="Button Text Colour"
                      value={values?.toolbarButtonTextColour ?? "#FFFFFF"}
                    />
                  </CompanyBrandingSection>
                  <CompanyBrandingSection
                    data-test-id="company-branding-form__button-colours-toggle-button"
                    sectionName="buttonColours"
                    label="Buttons"
                    hidden={!values?.isGeneratedCSSFile}
                  >
                    <CompanyBrandingColourPicker
                      data-test-id="company-branding-form__button-primary-colour-input"
                      name="buttonPrimaryColour"
                      label="Primary Colour"
                      value={values?.buttonPrimaryColour ?? "#FFFFFF"}
                    />
                    <CompanyBrandingColourPicker
                      data-test-id="company-branding-form__button-primary-text-colour-input"
                      name="buttonPrimaryTextColour"
                      label="Primary Text Colour"
                      value={values?.buttonPrimaryTextColour ?? "#FFFFFF"}
                    />
                    <CompanyBrandingColourPicker
                      data-test-id="company-branding-form__button-secondary-colour-input"
                      name="buttonSecondaryColour"
                      label="Secondary Colour"
                      value={values?.buttonSecondaryColour ?? "#FFFFFF"}
                    />
                    <CompanyBrandingColourPicker
                      data-test-id="company-branding-form__button-secondary-text-colour-input"
                      name="buttonSecondaryTextColour"
                      label="Secondary Text Colour"
                      value={values?.buttonSecondaryTextColour ?? "#FFFFFF"}
                    />
                    <CompanyBrandingColourPicker
                      data-test-id="company-branding-form__button-success-colour-input"
                      name="buttonSuccessColour"
                      label="Success Colour"
                      value={values?.buttonSuccessColour ?? "#FFFFFF"}
                    />
                    <CompanyBrandingColourPicker
                      data-test-id="company-branding-form__button-success-text-colour-input"
                      name="buttonSuccessTextColour"
                      label="Success Text Colour"
                      value={values?.buttonSuccessTextColour ?? "#FFFFFF"}
                    />
                    <CompanyBrandingColourPicker
                      data-test-id="company-branding-form__button-danger-colour-input"
                      name="buttonDangerColour"
                      label="Danger Colour"
                      value={values?.buttonDangerColour ?? "#FFFFFF"}
                    />
                    <CompanyBrandingColourPicker
                      data-test-id="company-branding-form__button-danger-text-colour-input"
                      name="buttonDangerTextColour"
                      label="Danger Text Colour"
                      value={values?.buttonDangerTextColour ?? "#FFFFFF"}
                    />
                  </CompanyBrandingSection>
                  <CompanyBrandingSection
                    data-test-id="company-branding-form__font-toggle-button"
                    sectionName="font"
                    label="Font"
                    hidden={!values?.isGeneratedCSSFile}
                  >
                    <CompanyBrandingColourPicker
                      data-test-id="company-branding-form__font-body-colour-input"
                      name="fontBodyColour"
                      label="Body Colour"
                      value={values?.fontBodyColour ?? "#000000"}
                    />
                    <CompanyBrandingColourPicker
                      data-test-id="company-branding-form__font-heading-colour-input"
                      name="fontHeadingColour"
                      label="Header Colour"
                      value={values?.fontHeadingColour ?? "#FFFFFF"}
                    />
                    <div className="flex-grow-1">
                      <label className="col-form-label fw-bold fs-6 me-2">Header Styles: </label>
                    </div>
                    <div>
                      <div className="d-flex gap-2">
                        <Field
                          data-test-id="company-branding-form__header-text-bold-checkbox"
                          name={"isHeaderTextBold"}
                          id="headerBoldCheck"
                          className={`btn-check ${values.isHeaderTextBold ? "font-btn-selected" : ""}`}
                          type="checkbox"
                        />
                        <label className="btn btn-primary" htmlFor="headerBoldCheck" title="Bold">
                          B
                        </label>
                        <Field
                          data-test-id="company-branding-form__header-text-italic-checkbox"
                          name={"isHeaderTextItalic"}
                          id="headerItalicCheck"
                          className={`btn-check ${values.isHeaderTextItalic ? "font-btn-selected" : ""}`}
                          type="checkbox"
                        />
                        <label className="btn btn-primary" htmlFor="headerItalicCheck" title="Italic">
                          I
                        </label>
                        <Field
                          data-test-id="company-branding-form__header-text-underlined-checkbox"
                          name={"isHeaderTextUnderlined"}
                          id="headerUnderlineCheck"
                          className={`btn-check ${values.isHeaderTextUnderlined ? "font-btn-selected" : ""}`}
                          type="checkbox"
                        />
                        <label className="btn btn-primary" htmlFor="headerUnderlineCheck" title="Underline">
                          U
                        </label>
                      </div>
                    </div>
                    <div className="flex-grow-1">
                      <label className="col-form-label fw-bold fs-6 me-2">Body Font File: </label>
                    </div>
                    <CompanyBrandingFileUpload
                      data-test-id="company-branding-form__body-font-file-upload"
                      name="bodyFont"
                      acceptedFileTypes={["ttf"]}
                      initialFiles={initialBodyFont}
                      setFileLocalURL={setBodyFontLocalURL}
                      maximumNumberOfFiles={1}
                      setFieldValue={setFieldValue}
                      onLoaded={() => {
                        const fontFile = new FontFace("PreviewBodyFont", `url(${bodyFontLocalURL})`);
                        document.fonts.add(fontFile);
                      }}
                    />
                    <div className="flex-grow-1">
                      <label className="col-form-label fw-bold fs-6 me-2">Header Font File: </label>
                    </div>
                    <CompanyBrandingFileUpload
                      data-test-id="company-branding-form__header-font-file-upload"
                      name="headerFont"
                      acceptedFileTypes={["ttf"]}
                      initialFiles={initialHeaderFont}
                      setFileLocalURL={setHeaderFontLocalURL}
                      maximumNumberOfFiles={1}
                      setFieldValue={setFieldValue}
                      onLoaded={() => {
                        const fontFile = new FontFace("HeaderBodyFont", `url(${headerFontLocalURL})`);
                        document.fonts.add(fontFile);
                      }}
                    />
                  </CompanyBrandingSection>
                  <CompanyBrandingSection
                    data-test-id="company-branding-form__images-toggle-button"
                    sectionName="images"
                    label="Images"
                    hidden={!values?.isGeneratedCSSFile}
                  >
                    <div className="flex-grow-1">
                      <label className="col-form-label fw-bold fs-6 me-2">Logo: </label>
                    </div>
                    <CompanyBrandingFileUpload
                      data-test-id="company-branding-form__logo-file-upload"
                      name="logo"
                      acceptedFileTypes={["png"]}
                      initialFiles={initialLogo}
                      setFileLocalURL={setLogoLocalURL}
                      maximumNumberOfFiles={1}
                      setFieldValue={setFieldValue}
                    />
                    <div className="flex-grow-1">
                      <label className="col-form-label fw-bold fs-6 me-2">Favicon: </label>
                    </div>
                    <CompanyBrandingFileUpload
                      data-test-id="company-branding-form__favicon-file-upload"
                      name="favicon"
                      acceptedFileTypes={["png"]}
                      initialFiles={initialFavicon}
                      maximumNumberOfFiles={1}
                      setFieldValue={setFieldValue}
                    />
                    <div className="flex-grow-1">
                      <label className="col-form-label fw-bold fs-6 me-2">Custom CSS: </label>
                    </div>
                    <div>
                      <Field name={"companyBrandingFile"}>
                        {() => (
                          <CPLFileUpload
                            data-test-id="company-branding-form__css-file-upload"
                            maximumNumberOfFiles={1}
                            initialFiles={initialCssFile}
                            updateNewFiles={(file) => {
                              setFieldValue("companyBrandingFile", { data: file[0], fileName: file[0]?.name ?? null });
                            }}
                          />
                        )}
                      </Field>
                    </div>
                  </CompanyBrandingSection>
                </div>
              </div>
              <CompanyBrandingPreview
                isPreviewOpen={isPreviewOpen}
                setPreviewOpen={setPreviewOpen}
                values={values}
                logoLocalURL={logoLocalURL}
              />
              <div className="card-footer"></div>
            </Form>
          )}
        </Formik>
      </ActivityIndicator>
    </div>
  );
};

type CompanyBrandingColourPickerProps = {
  name: string;
  value: string;
  label: string;
  "data-test-id"?: string;
};

const CompanyBrandingColourPicker = (props: CompanyBrandingColourPickerProps) => {
  return (
    <div className="d-flex align-items-center">
      <Field
        data-test-id={props["data-test-id"]}
        name={props.name}
        type="color"
        value={props.value}
        className="colour-picker me-4"
      />
      <div>
        <label className="col-form-label fw-bold fs-6 me-2">{props.label}</label>
      </div>
    </div>
  );
};

type CompanyBrandingSectionProps = {
  children: React.ReactNode;
  hidden: boolean;
  sectionName: string;
  label: string;
  "data-test-id"?: string;
};

const CompanyBrandingSection = (props: CompanyBrandingSectionProps) => {
  return (
    <fieldset className="accordion-item" hidden={props.hidden}>
      <span className="accordion-header" id={`${props.sectionName}Header`}>
        <button
          data-test-id={props["data-test-id"]}
          className="accordion-button accordion-button-overrides fs-1 fw-bolder text-dark mb-3 collapsed"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target={`#${props.sectionName}`}
          aria-expanded="true"
          aria-controls={props.sectionName}
        >
          {props.label}
        </button>
      </span>
      <div id={props.sectionName} className="accordion-collapse collapse px-2 pb-2">
        {props.children}
      </div>
    </fieldset>
  );
};

type CompanyBrandingFileUploadProps = {
  name: string;
  initialFiles: ExistingFile[] | undefined;
  acceptedFileTypes: string[];
  maximumNumberOfFiles: number;
  setFileLocalURL?: React.Dispatch<React.SetStateAction<string | null | undefined>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void;
  onLoaded?: () => void;
  "data-test-id"?: string;
};

const CompanyBrandingFileUpload = (props: CompanyBrandingFileUploadProps) => {
  return (
    <CPLFileUpload
      data-test-id={props["data-test-id"]}
      acceptedFileTypes={props.acceptedFileTypes}
      maximumNumberOfFiles={props.maximumNumberOfFiles}
      initialFiles={props.initialFiles}
      updateNewFiles={(file) => {
        if (file[0]) {
          const fileReader = new window.FileReader();
          fileReader.addEventListener(
            "load",
            () => {
              if (props.setFileLocalURL) {
                props.setFileLocalURL(fileReader.result as string);
              }
            },
            false
          );
          fileReader.readAsDataURL(file[0]);
          if (props.onLoaded) {
            props.onLoaded();
          }
          props.setFieldValue(props.name, { data: file[0], fileName: file[0]?.name });
        }
      }}
    />
  );
};

export default CompanyBrandingForm;
